/**
 * パスワード再設定・招待の受諾（**認証不要**の着地ページ）。
 *
 * ここが無いあいだ、パスワード再設定は**行き止まり**だった。
 * `POST /auth/portal/password/forgot` が送るメールのリンクは
 * `${PORTAL_ORIGIN}/reset-password?token=...`（`auth/portal.ts`）で、
 * API も揃っているのに **SPA にそのパスが無く 404** になっていた。
 * ログインできなくなった契約者が自力で戻る手段がゼロという状態で、
 * 「サポートへ連絡」以外の出口が無かった。
 *
 * 招待（`/invite?token=...`）も同じ形なので 1 枚で兼ねる。違うのは
 *   - 文言（「再設定」ではなく「はじめてのパスワード設定」）
 *   - 叩く API（`invite/accept`。未配備なら `password/reset` へ落ちる）
 * の 2 点だけで、入力させるものは同一。画面を 2 枚に割ると、
 * 強度の規則や成功後の導線が必ず片方だけ古くなる。
 *
 * 守っていること:
 *   1. **トークンをフォームの値として画面に残さない。** URL から読み、
 *      送信にだけ使う（コピーして共有される事故を減らす）。
 *   2. 成功しても自動ログインしない。サーバは再設定と同時に全セッションを
 *      切るので、ログイン画面へ誘導するのが唯一の正しい導線。
 *   3. 強度の最終判断はサーバ（`assertPasswordPolicy`）。画面は最低長と
 *      一致確認だけを見て往復を 1 回減らす。
 */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
} from '@mail-sv/ui';
import { CheckCircle2, KeyRound, Mail } from 'lucide-react';
import { acceptInvite, previewInvite, resetPassword } from '../lib/auth.js';
import { FormAlert, SubmitBar, type FieldErrors, hasErrors } from '../components/form.js';
import { PasswordStrength } from '../components/password-field.js';
import { useAppNavigate, useSearchParams } from '../lib/nav.js';
import { env } from '../env.js';

/** パスワードの最低長。サーバ（`assertPasswordPolicy`）と揃える。 */
const MIN_LENGTH = 12;

type FormKey = 'password' | 'confirm';

interface Copy {
  title: string;
  description: string;
  submitLabel: string;
  doneTitle: string;
  doneBody: string;
  /** リンクの有効期限。再設定は 1 時間、招待は 7 日と**別物**なので混ぜない。 */
  expiry: string;
}

const COPY: Record<'reset' | 'invite', Copy> = {
  reset: {
    title: 'パスワードの再設定',
    description: 'メールでお送りしたリンクから、新しいパスワードを設定してください。',
    submitLabel: 'パスワードを設定する',
    doneTitle: 'パスワードを設定しました',
    doneBody:
      '安全のため、これまでのログインはすべて無効になりました。新しいパスワードでログインしてください。',
    expiry: 'リンクの有効期限は 1 時間です。',
  },
  invite: {
    title: 'パスワードの設定（招待）',
    description:
      'コントロールパネルへの招待を受け取りました。ログインに使うパスワードを設定してください。',
    submitLabel: '設定してはじめる',
    doneTitle: 'パスワードを設定しました',
    doneBody:
      '設定したメールアドレスとパスワードでログインできます。メールの送受信に使うアドレスとは別のものです。',
    expiry: '招待リンクの有効期限は 7 日間です。',
  },
};

export function ResetPasswordPage({ mode = 'reset' }: { mode?: 'reset' | 'invite' }) {
  const search = useSearchParams();
  const navigate = useAppNavigate();
  const copy = COPY[mode];

  // トークンは URL からだけ読む（state には入れない）。
  const token = typeof search.token === 'string' ? search.token : '';

  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [errors, setErrors] = React.useState<FieldErrors<FormKey>>({});

  /*
   * **パスワードマネージャに渡すユーザー名。**
   *
   * この画面はトークンしか持たないので、宛先を照会しないとフォームに
   * username に相当する項目が 1 つも無い。すると 1Password / Chrome は
   * **ユーザー名を空欄で保存する**（2026-09-05 に報告された）。
   * 利用者は後から手で直すことになり、直さなければ次回の自動入力が効かない。
   *
   * **引けなくても画面は止めない。** 宛先が分からないだけでパスワードは
   * 設定できる。古い API の環境では 404 になるので、そのときも空のまま進む。
   */
  const [accountEmail, setAccountEmail] = React.useState('');
  React.useEffect(() => {
    if (token.length === 0) return;
    let alive = true;
    void previewInvite(token).then((r) => {
      if (alive && r?.email) setAccountEmail(r.email);
    });
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = useMutation({
    mutationFn: () => (mode === 'invite' ? acceptInvite(token, password) : resetPassword(token, password)),
  });

  function validate(): FieldErrors<FormKey> {
    const next: FieldErrors<FormKey> = {};
    if (password.length < MIN_LENGTH) next.password = `${MIN_LENGTH} 文字以上で設定してください`;
    if (confirm !== password) next.confirm = 'パスワードが一致しません';
    return next;
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (hasErrors(next)) return;
    submit.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <div className="brand-gradient mx-auto flex size-12 items-center justify-center rounded-xl text-white shadow-lg shadow-primary/25">
            {mode === 'invite' ? (
              <Mail className="size-6" aria-hidden />
            ) : (
              <KeyRound className="size-6" aria-hidden />
            )}
          </div>
          <h1 className="text-xl font-semibold">{env.brandName}</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{copy.title}</CardTitle>
            <CardDescription>{copy.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submit.isSuccess ? (
              <>
                <Alert variant="success">
                  <AlertTitle className="flex items-center gap-2">
                    <CheckCircle2 className="size-4" aria-hidden />
                    {copy.doneTitle}
                  </AlertTitle>
                  <AlertDescription>{copy.doneBody}</AlertDescription>
                </Alert>
                <Button className="w-full" onClick={() => navigate({ to: '/login' })}>
                  ログイン画面へ
                </Button>
              </>
            ) : token.length === 0 ? (
              /*
               * トークンが無い＝メールのリンクを経由していない。
               * 空のフォームを出すと「入力したのに設定できない」で終わるので、
               * 再送の導線（ログイン画面の「パスワードをお忘れですか」）へ返す。
               */
              <>
                <Alert variant="warning">
                  <AlertTitle>リンクが正しくありません</AlertTitle>
                  <AlertDescription>
                    メール内のリンクをそのまま開いてください。{copy.expiry}
                    期限が切れている場合は、
                    {mode === 'invite'
                      ? '組織の管理者に招待をやり直してもらってください。'
                      : 'ログイン画面から再度お手続きください。'}
                  </AlertDescription>
                </Alert>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">ログイン画面へ</Link>
                </Button>
              </>
            ) : (
              <form className="space-y-4" onSubmit={onSubmit} noValidate>
                <FormAlert error={submit.error} />

                {/*
                  **パスワードマネージャ用のユーザー名。**

                  `autocomplete="username"` を持つ項目が同じフォームに無いと、
                  1Password / Chrome は保存時にユーザー名を空欄にする。

                  `type="hidden"` や `display:none` では**拾わない実装がある**
                  ので、読み取り専用の可視項目として置く。利用者にとっても
                  「どのアカウントのパスワードを設定しているのか」が分かる
                  （招待は複数のアドレスに届きうる）。

                  照会に失敗したときは項目ごと出さない。空欄の入力欄を見せても
                  混乱するだけで、パスワードマネージャにも何も渡らない。
                */}
                {accountEmail !== '' && (
                  <Field label="アカウント" htmlFor="rp-username">
                    <Input
                      id="rp-username"
                      name="username"
                      type="email"
                      autoComplete="username"
                      value={accountEmail}
                      readOnly
                      // 読み取り専用なので、タブ移動の邪魔にならないよう外す。
                      // パスワードマネージャは tabIndex を見ないので拾える。
                      tabIndex={-1}
                      className="bg-muted"
                    />
                  </Field>
                )}

                <Field
                  label="新しいパスワード"
                  htmlFor="rp-password"
                  required
                  error={errors.password}
                  hint={`${MIN_LENGTH} 文字以上。メールアドレスと似た文字列は使えません。`}
                >
                  <Input
                    id="rp-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    autoFocus
                  />
                </Field>

                <PasswordStrength value={password} />

                <Field
                  label="新しいパスワード（確認）"
                  htmlFor="rp-confirm"
                  required
                  error={errors.confirm}
                >
                  <Input
                    id="rp-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                  />
                </Field>

                <Alert variant="info">
                  <AlertTitle>メールのパスワードとは別です</AlertTitle>
                  <AlertDescription>
                    ここで設定するのは、このコントロールパネルにログインするためのパスワードです。
                    メールソフトに設定するパスワードは変わりません。
                  </AlertDescription>
                </Alert>

                <SubmitBar
                  submitLabel={copy.submitLabel}
                  pending={submit.isPending}
                  disabled={password.length < MIN_LENGTH || confirm !== password}
                />
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="underline">
            ログイン画面へ戻る
          </Link>
        </p>
      </div>
    </div>
  );
}

/** 招待メールの着地点。中身は再設定と同じで、文言と API だけが違う。 */
export function InvitePage() {
  return <ResetPasswordPage mode="invite" />;
}
